import React, { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Layout from "../components/Layout";
import Loading from "../components/Loading";
import { useAuth } from "../context/AuthContext";

// Lazy-loaded components
const AdminLogin = lazy(() => import("../pages/AdminLogin"));
const Dashboard = lazy(() => import("../pages/Dashboard"));
const UserManagement = lazy(() =>
  import("../pages/UserManagement").catch(() => ({
    default: () => <div>User Management - Coming Soon</div>,
  }))
);
const Settings = lazy(() =>
  import("../pages/Settings").catch(() => ({
    default: () => <div>Settings - Coming Soon</div>,
  }))
);
const Vendors = lazy(() =>
  import("../pages/Vendors").catch(() => ({
    default: () => <div>Vendors - Coming Soon</div>,
  }))
);
const Riders = lazy(() =>
  import("../pages/Riders").catch(() => ({
    default: () => <div>Riders - Coming Soon</div>,
  }))
);
const Orders = lazy(() =>
  import("../pages/Orders").catch(() => ({
    default: () => <div>Orders - Coming Soon</div>,
  }))
);
const Finance = lazy(() =>
  import("../pages/Finance").catch(() => ({
    default: () => <div>Finance - Coming Soon</div>,
  }))
);
const Inbox = lazy(() =>
  import("../pages/Inbox").catch(() => ({
    default: () => <div>Inbox - Coming Soon</div>,
  }))
);
const VendorRequests = lazy(() =>
  import("../pages/VendorRequests").catch(() => ({
    default: () => <div>Vendor Requests - Coming Soon</div>,
  }))
);
const Notifications = lazy(() =>
  import("../pages/Notifications").catch(() => ({
    default: () => <div>Notifications - Coming Soon</div>,
  }))
);
const Complaints = lazy(() =>
  import("../pages/Complaints").catch(() => ({
    default: () => <div>Complaints - Coming Soon</div>,
  }))
);
const NotFound = lazy(() =>
  import("../pages/NotFound").catch(() => ({
    default: () => <div>404 - Page Not Found</div>,
  }))
);

// Error Boundary Component
class ErrorBoundary extends React.Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: "20px", textAlign: "center" }}>
          <h1>Something went wrong</h1>
          <p>{this.state.error?.message || "An unexpected error occurred."}</p>
          <button onClick={() => window.location.reload()}>Reload Page</button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Private Route Component
const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <Loading />;
  }

  return user ? children : <Navigate to="/" />;
};

const AppRoutes = () => {
  return (
    <Suspense fallback={<Loading />}>
      <ErrorBoundary>
        <Routes>
          {/* Public Route */}
          <Route path="/" element={<AdminLogin />} />

          {/* Protected Routes with Layout */}
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/vendors"
            element={
              <PrivateRoute>
                <Layout>
                  <Vendors />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/riders"
            element={
              <PrivateRoute>
                <Layout>
                  <Riders />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/orders"
            element={
              <PrivateRoute>
                <Layout>
                  <Orders />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/inbox"
            element={
              <PrivateRoute>
                <Layout>
                  <Inbox />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/vendor-requests"
            element={
              <PrivateRoute>
                <Layout>
                  <VendorRequests />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <PrivateRoute>
                <Layout>
                  <Notifications />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/complaints"
            element={
              <PrivateRoute>
                <Layout>
                  <Complaints />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <PrivateRoute>
                <Layout>
                  <Settings />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/finance"
            element={
              <PrivateRoute>
                <Layout>
                  <Finance />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/users"
            element={
              <PrivateRoute>
                <Layout>
                  <UserManagement />
                </Layout>
              </PrivateRoute>
            }
          />

          {/* 404 Catch-All */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </ErrorBoundary>
    </Suspense>
  );
};

export default AppRoutes;